import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormBuilder, FormControl, ReactiveFormsModule } from '@angular/forms';

import { IconModule, SelectModule, SimpleInputModule } from '@hglobal/fin-ui';
import { TranslateModule } from '@ngx-translate/core';
import { distinctUntilChanged } from 'rxjs';

import { ButtonComponent } from '@iw/common';
import { limitIntegerDigits } from '@iw/core/helpers';
import { AmountConversionService } from '@iw/core/services';
import { ShellStore } from '@iw/shell';
import { DictionariesStore } from '@iw/store';

const MAX_DECIMALS = 6;

@Component({
  selector: 'iw-currency-rate-content',
  standalone: true,
  imports: [
    ButtonComponent,
    SelectModule,
    TranslateModule,
    IconModule,
    SimpleInputModule,
    ReactiveFormsModule,
  ],
  templateUrl: './currency-rate-content.component.html',
  styleUrls: ['./currency-rate-content.component.scss'],
  host: { '[class.dropdownView]': 'dropdownView()' },
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CurrencyRateContentComponent {
  readonly #dictStore = inject(DictionariesStore);
  readonly #shellStore = inject(ShellStore);
  readonly #fb = inject(FormBuilder);
  readonly #amountConversionService = inject(AmountConversionService);

  readonly dropdownView = input(false);

  currencies = this.#dictStore.currencyDictionary;
  defaultCurrency = this.#shellStore.defaultCurrency;
  lastEdited = signal<'from' | 'to'>(null);

  readonly fromDefaultValue = computed(() => {
    return {
      amount: '1',
      currency: this.defaultCurrency(),
    };
  });
  readonly toDefaultValue = computed(() => {
    return {
      amount: '',
      currency: this.defaultCurrency() === 'USD' ? 'EUR' : 'USD',
    };
  });

  form = this.#fb.group({
    from: this.#fb.nonNullable.group(this.fromDefaultValue()),
    to: this.#fb.nonNullable.group(this.toDefaultValue()),
  });

  readonly fromControl = this.form.controls.from;
  readonly toControl = this.form.controls.to;

  readonly fromControlChanges = toSignal(this.fromControl.valueChanges, {
    initialValue: this.fromControl.value,
  });

  readonly toControlChanges = toSignal(this.toControl.valueChanges, {
    initialValue: this.toControl.value,
  });

  readonly fromCurrencyChanges = toSignal(
    this.fromControl.controls.currency.valueChanges.pipe(
      distinctUntilChanged(),
    ),
    { initialValue: this.fromControl.controls.currency.value },
  );

  readonly toCurrencyChanges = toSignal(
    this.toControl.controls.currency.valueChanges.pipe(distinctUntilChanged()),
    { initialValue: this.toControl.controls.currency.value },
  );

  readonly fromCurrencyList = computed(() => {
    return this.currencies().filter(
      ({ id }) => id !== this.toCurrencyChanges(),
    );
  });

  readonly toCurrencyList = computed(() => {
    return this.currencies().filter(
      ({ id }) => id !== this.fromCurrencyChanges(),
    );
  });

  readonly fromCurrency = computed(() => ({
    ...this.#dictStore.getCurrency(this.fromCurrencyChanges(), 'symbol'),
    decimals: MAX_DECIMALS,
  }));

  readonly toCurrency = computed(() => ({
    ...this.#dictStore.getCurrency(this.toCurrencyChanges(), 'symbol'),
    decimals: MAX_DECIMALS,
  }));

  readonly syncFromToEffect = effect(() => {
    if (this.lastEdited() !== 'from') return;

    const from = this.fromControlChanges();
    const to = this.toControl.value;

    if (!to.amount && !from.amount) return;

    const limitedAmount = limitIntegerDigits(from.amount);

    if (from.amount !== limitedAmount) {
      this.fromControl.patchValue(
        { amount: limitedAmount },
        { emitEvent: false },
      );
    }

    const nextAmount = this.#amountConversionService.convert(
      from.amount,
      this.fromCurrency(),
      this.toCurrency(),
      'fromTo',
    );

    if (Number(to.amount) === Number(nextAmount)) return;

    this.toControl.patchValue({ amount: nextAmount }, { emitEvent: false });
  });

  readonly syncToFromEffect = effect(() => {
    if (this.lastEdited() !== 'to') return;

    const to = this.toControlChanges();
    const from = this.fromControl.value;

    if (!to.amount && !from.amount) return;

    const limitedAmount = limitIntegerDigits(to.amount);

    if (to.amount !== limitedAmount) {
      this.toControl.patchValue(
        { amount: limitedAmount },
        { emitEvent: false },
      );
    }

    const nextAmount = this.#amountConversionService.convert(
      to.amount,
      this.fromCurrency(),
      this.toCurrency(),
      'toFrom',
    );

    if (Number(from.amount) === Number(nextAmount)) return;

    this.fromControl.patchValue({ amount: nextAmount }, { emitEvent: false });
  });

  get fromAmountCtrl(): FormControl<string> {
    return this.fromControl.get('amount') as FormControl<string>;
  }

  get toAmountCtrl(): FormControl<string> {
    return this.toControl.get('amount') as FormControl<string>;
  }

  normalizeOnFocusOut(event: Event, control: FormControl<string>): void {
    //'0.0' -> '0' this is an edge case which is not expected on the default mask behavior, will be moved to the custom directive later
    const input = event.target as HTMLInputElement;
    if (input.value === '0.0' || input.value === '0.00') {
      control.patchValue('0', { emitEvent: false });
    }

    if (input.value.endsWith('.')) {
      control.patchValue(control.value.slice(0, -1), { emitEvent: false });
    }
  }

  swap() {
    this.form.patchValue({
      from: { ...this.toControl.value },
      to: { ...this.fromControl.value },
    });
  }

  refreshForm(): void {
    this.fromControl.patchValue(this.fromDefaultValue());
    this.toControl.patchValue(this.toDefaultValue());
  }

  refreshRates(): void {
    this.#dictStore.refreshRates();
  }

  onDropdownOpened(): void {
    this.refreshForm();
    this.refreshRates();
    this.#initConversion();
  }

  #initConversion(): void {
    this.lastEdited.set('from');
  }
}
